import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import AppBarContainer from 'bento-ordering/base/containers/AppBarContainer';
import Box from '@material-ui/core/Box';
import Footer from 'bento-ordering/base/ui_components/Footer';
import { isMobileMedia } from 'bento-ordering/utils/media';
import MenuPageFooter from 'bento-ordering/menus/containers/MenuPageFooter';
import GridContainer from 'bento-ordering/base/ui_components/GridContainer';
import Hero from 'bento-ordering/base/ui_components/Hero';
import SecondaryCta from 'bento-ordering/base/components/SecondaryCta';
import {
  DEFAULT_LAYOUT_PADDING,
  MENU_LAYOUT_PADDING
} from 'bento-ordering/base/style_mixins/layoutPadding';
import { CHILDREN_PROPTYPES } from 'bento-ordering/base/constants/propTypes';
import MenuSectionsBar from 'bento-ordering/menus/containers/MenuSectionsBar';
import LocationsCustomMessage from 'bento-ordering/locations/components/LocationsCustomMessage';
import { isSoleEntity } from 'bento-ordering/base/reducers/bentoApi';
import { getCustomMessageSelector } from 'bento-ordering/account/reducers/index';
import { MAX_MENU_WIDTH } from 'bento-ordering/base/constants/maxWidth';

class BaseLayout extends React.PureComponent {
  static propTypes = {
    children: CHILDREN_PROPTYPES.isRequired,
    mobileFooter: CHILDREN_PROPTYPES,
    hideHeroOnMobile: PropTypes.bool,
    hideNav: PropTypes.bool,
    hideBrandFooterOnMobile: PropTypes.bool,
    hideMobileStickyCtaFooter: PropTypes.bool,
    heroSrc: PropTypes.string,
    heroAlt: PropTypes.string,
    layoutPadding: PropTypes.object,
    isModal: PropTypes.bool,
    isFetching: PropTypes.bool,
    utmFooterLink: PropTypes.string,
    heroContent: CHILDREN_PROPTYPES,
    id: PropTypes.string,
    shouldUseMenuStyle: PropTypes.bool,
    isSingleLocation: PropTypes.bool,
    customMessage: PropTypes.string
  };

  static defaultProps = {
    mobileFooter: <MenuPageFooter />,
    layoutPadding: DEFAULT_LAYOUT_PADDING,
    shouldUseMenuStyle: false
  };

  renderNav = () => {
    if (this.props.hideNav) {
      return null;
    }
    return <AppBarContainer />;
  };

  renderMobileFooter = () => {
    if (this.props.hideMobileStickyCtaFooter || this.props.isFetching) {
      return null;
    }

    return this.props.mobileFooter;
  };

  renderHero() {
    const { hideHeroOnMobile, heroSrc, heroAlt, heroContent } = this.props;

    const shouldDisplayOnMobile = !(isMobileMedia() && hideHeroOnMobile);
    const noHeroContent = !!(heroSrc || heroContent);

    // couple of cases where we don't display hero
    // 1) if current viewport is mobile, and the prop has been set to hide on mobile
    // 2) if there is no hero content, which can include an image, or other specified
    // hero content (like a dom node containing a search bar on location list page)
    const shouldDisplayHero = shouldDisplayOnMobile && noHeroContent;

    if (shouldDisplayHero) {
      return (
        <Hero src={heroSrc} alt={heroAlt}>
          {heroContent}
        </Hero>
      );
    }
    return null;
  }

  renderMenuSectionsBar() {
    const { shouldUseMenuStyle } = this.props;

    if (shouldUseMenuStyle) {
      return <MenuSectionsBar />;
    }
    return null;
  }

  /**
   * render the menu container custom text
   */
  renderMenuCustomText() {
    const { shouldUseMenuStyle, customMessage, isSingleLocation } = this.props;

    if (shouldUseMenuStyle) {
      if (customMessage && isSingleLocation) {
        return (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              style={{
                width: MAX_MENU_WIDTH,
                borderBottom: '1px solid #E5E5E6'
              }}
            >
              <Box
                textAlign="center"
                pt={3}
                ml={2}
                mr={2}
                style={{
                  maxWidth: MAX_MENU_WIDTH
                }}
              >
                <LocationsCustomMessage message={customMessage} />
              </Box>
            </Box>
          </Box>
        );
      }
    }
  }

  render() {
    const {
      layoutPadding,
      isModal,
      isFetching,
      hideBrandFooterOnMobile,
      children,
      utmFooterLink,
      id,
      shouldUseMenuStyle
    } = this.props;

    const padding = shouldUseMenuStyle ? MENU_LAYOUT_PADDING : layoutPadding;
    return (
      <Box pt={isModal ? {} : { xs: 7, sm: 8 }}>
        {this.renderNav()}
        {this.renderHero()}
        {this.renderMenuCustomText()}
        {this.renderMenuSectionsBar()}
        <GridContainer {...padding}>
          <div id={id}>{children}</div>

          {!isFetching && isFetching !== undefined && (
            <Footer hideBrandFooterOnMobile={hideBrandFooterOnMobile}>
              <SecondaryCta
                label="Powered By Bentobox"
                component="a"
                href={utmFooterLink}
                dataTestId="footer"
              />
            </Footer>
          )}
          {this.renderMobileFooter()}
        </GridContainer>
      </Box>
    );
  }
}

const mapStateToProps = state => {
  const utmFooterLink = get(state.account, 'utm_footer_link', '');
  const account = state.account;
  const restaurantLocations = state.restaurantLocations;

  return {
    utmFooterLink,
    customMessage: getCustomMessageSelector(account),
    isSingleLocation: isSoleEntity(restaurantLocations)
  };
};

export default connect(mapStateToProps)(BaseLayout);
